import Details from './details'
import {screenLarge} from '../utils'

export default class Tabs {
    constructor() {
        document.querySelectorAll<HTMLElement>('.ce-tabs').forEach((el) => new TabGroup(el))
    }
}

class TabGroup {
    private itemsEl: HTMLElement | null
    private items: HTMLDetailsElement[]
    private summaries: HTMLElement[]
    private detailsInstances: Details[] = []
    private isDesktop: boolean | null = null
    private summaryClick: (e: Event) => void
    private summaryKeydown: (e: KeyboardEvent) => void
    private onResize: () => void

    constructor(root: HTMLElement) {
        this.itemsEl = root.querySelector('.ce-tabs__items')
        this.items = Array.from(root.querySelectorAll<HTMLDetailsElement>('.ce-tabs__item'))
        this.summaries = this.items.map((item) => item.querySelector('summary') as HTMLElement)
        if (!this.items.length) return

        this.summaryClick = (e) => this.onDesktopClick(e)
        this.summaryKeydown = (e) => this.onDesktopKeydown(e)
        this.onResize = () => this.syncHeight()

        this.apply(screenLarge.matches)
        screenLarge.addEventListener('change', (e) => this.apply(e.matches))
        window.addEventListener('resize', this.onResize)
    }

    private apply(isDesktop: boolean) {
        if (isDesktop === this.isDesktop) return
        this.isDesktop = isDesktop

        if (isDesktop) {
            this.teardownMobile()
            this.setupDesktop()
        } else {
            this.teardownDesktop()
            this.setupMobile()
        }
    }

    private setupMobile() {
        this.detailsInstances = this.items.map((item) => new Details(item))
    }

    private teardownMobile() {
        this.detailsInstances.forEach((instance) => instance.destroy())
        this.detailsInstances = []
    }

    private setupDesktop() {
        let active = this.items.findIndex((item) => item.open)
        if (active === -1) active = 0
        this.select(active)

        this.summaries.forEach((summary) => {
            summary.addEventListener('click', this.summaryClick)
            summary.addEventListener('keydown', this.summaryKeydown)
        })
    }

    private teardownDesktop() {
        this.summaries.forEach((summary) => {
            summary.removeEventListener('click', this.summaryClick)
            summary.removeEventListener('keydown', this.summaryKeydown)
        })
        if (this.itemsEl) this.itemsEl.style.minHeight = ''
    }

    private select(index: number) {
        this.items.forEach((item, i) => (item.open = i === index))
        this.syncHeight()
    }

    private syncHeight() {
        if (!this.isDesktop || !this.itemsEl) return
        const open = this.items.find((item) => item.open)
        const panel = open?.querySelector<HTMLElement>('.ce-tabs__panel')
        this.itemsEl.style.minHeight = panel ? `${panel.offsetHeight}px` : ''
    }

    private onDesktopClick(e: Event) {
        e.preventDefault()
        const index = this.summaries.indexOf(e.currentTarget as HTMLElement)
        if (index !== -1) this.select(index)
    }

    private onDesktopKeydown(e: KeyboardEvent) {
        const current = this.summaries.indexOf(e.currentTarget as HTMLElement)
        if (current === -1) return

        let next: number | null = null
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                next = (current + 1) % this.summaries.length
                break
            case 'ArrowLeft':
            case 'ArrowUp':
                next = (current - 1 + this.summaries.length) % this.summaries.length
                break
            case 'Home':
                next = 0
                break
            case 'End':
                next = this.summaries.length - 1
                break
            default:
                return
        }

        e.preventDefault()
        this.select(next)
        this.summaries[next].focus()
    }
}
